import { defaultDropdownMenuItemBuilder } from "./menuItemBuilders.js";
import { MenuItem } from "prosemirror-menu";
import { toggleMark } from "prosemirror-commands";
import OrderedMenuItem from "../OrderedMenuItem.js";

/**
 * Abstract class providing the API for the editor actions.
 * An action is a pluggable brick that provides new functionality
 * to an editor. Such a functionality can be new nodes, new keybindings
 * and/or new menu items.
 *
 * This class also provide helping methods for the subclasses, mostly to build
 * menu items.
 *
 * @abstract
 * @param {function} [dropdownMenuItemBuilder] - Helper used to
 *   make a new dropdown item. Defaults to the one provided by
 *   ProseMirror. It takes a list of items as a `content` and an
 *   `options` object, both as arguments (to follow the ProseMirror
 *   structure). It returns a dropdown object that can be rendered as a
 *   menu item. `content` can also be a promise.
 */
export default class AbstractAction {
	constructor({ dropdownMenuItemBuilder } = {}) {
		this._dropdownMenuItemBuilder = dropdownMenuItemBuilder;
	}

	ensureDropdownMenuItemBuilder(dropdownMenuItemBuilder) {
		if (this._dropdownMenuItemBuilder) return;

		this._dropdownMenuItemBuilder =
			dropdownMenuItemBuilder || defaultDropdownMenuItemBuilder;
	}

	/**
	 * Return a list of menu items to append to the menu bar.
	 * When a falsy value is returned, no menu item is added.
	 *
	 * @param {object} schema - Schema generated by the WysiwygStateBuilder
	 * @return {null|OrderedMenuItem[]}
	 */
	getMenuItems({ schema: _schema }) {
		return null;
	}

	/**
	 * Return an object containing new prosemirror nodes.
	 * This will add support for those nodes in the editor.
	 *
	 * @return {{[key: string]: ProseMirror.Node}}
	 */
	getNodes() {
		return {};
	}

	/**
	 * Return an object containing new prosemirror marks.
	 * This will add support for those marks in the editor.
	 *
	 * @return {{[key: string]: ProseMirror.Mark}}
	 */
	getMarks() {
		return {};
	}

	/**
	 * Return an object containing the prosemirror nodeviews.
	 * They are used to fine-tuned how to display a node.
	 *
	 * @return {{[key: string]: ProseMirror.NodeView}}
	 */
	getNodeViews() {
		return {};
	}

	/**
	 * Return a list of prosemirror plugins to register to the editor.
	 * The registration order is important.
	 *
	 * @param {ActionsManager} actionsManager - The manager holding this action
	 * @param {Schema} schema - prosemirror schema built by WysiwygStateBuilder
	 * @return {Plugin[]}
	 */
	getPlugins({ actionsManager: _actionsManager, schema: _schema }) {
		return [];
	}

	/**
	 * Helping method to create a menu item.
	 *
	 * @param {number} [order] - Order of the menu item
	 * @param {object} ...menuSpec - Spec passed to the constructor of MenuItem
	 * @return {OrderedMenuItem}
	 */
	_makeMenuItem({ order, ...menuSpec }) {
		return new OrderedMenuItem({
			order,
			menuItem: new MenuItem(menuSpec),
		});
	}

	/**
	 * Helping method to create a dropdown menu item. Its structure is
	 * compatible with ProseMirror's dropdown and
	 * WysiwygEditorWidget's dropdown alike (depending on the
	 * `_dropdownMenuItemBuilder` chosen on initialization).
	 *
	 * @param {object} options - Arguments that should be passed to
	 * the dropdown. It contains at least a `label` field to be
	 * rendered on the button, and an `items` field that contains all
	 * the elements to be rendered in the list
	 */
	_makeDropdownMenuItem(options) {
		return this._dropdownMenuItemBuilder(options);
	}

	/**
	 * Build an icon DOM node for a menu item.
	 *
	 * @param {string} cssClass - CSS class for the icon, most probably a FontAwesome definition
	 * @return {{dom: HTMLElement}}
	 */
	_makeIcon({ cssClass }) {
		const icon = document.createElement("i");
		icon.className = cssClass;
		return { dom: icon };
	}

	/**
	 * Build a menu item specifically for a mark.
	 *
	 * @param {number} order - MenuItem order
	 * @param {string} title - Title of the menu item
	 * @param {object} icon - Icon of the menu item, built by `_makeIcon` by example
	 * @param {ProseMirror.Mark} mark - Mark to act on when the user interacts with the menu item
	 * @return {OrderedMenuItem}
	 */
	_makeMarkItem({ order, title, icon, mark }) {
		const command = toggleMark(mark);
		return new OrderedMenuItem({
			order,
			menuItem: new MenuItem({
				title,
				icon,
				active: (state) => this._isMarkActive({ state, type: mark }),
				run: command,
				enable: command,
			}),
		});
	}

	/**
	 * Check if a Mark is active in the current selection.
	 *
	 * @param {ProseMirror.EditorState} state - Current state of the editor
	 * @param {ProseMirror.Mark} type - Mark to check
	 * @return {boolean}
	 */
	_isMarkActive({ state, type }) {
		const { from, $from, to, empty } = state.selection;

		if (empty) {
			return type && type.isInSet(state.storedMarks || $from.marks());
		}

		return state.doc.rangeHasMark(from, to, type);
	}

	/**
	 * Add an update parameter to a dropdown item which will be
	 * highlighted if the current selection corresponds to the
	 * item. This is corresponding to the role of ProseMirror's
	 * `enable` parameter. Updating the item checks if its `run`
	 * parameter corresponds to the current state of the editor, then
	 * it adds a `selected` parameter if relevant.
	 *
	 * @param {object} item - Dropdown item that should be updated
	 *   according to ProseMirror's state
	 */
	_ensureItemCanBeEnabled(item) {
		item.update = (state) => {
			item.selected = item.run(state) ? null : true;
		};

		return item;
	}
}
